import { Box, Group, InputAddon, useFieldContext } from '@chakra-ui/react';
import type { SystemStyleObject } from '@chakra-ui/react';
import VCalculatorInput from '@voly/ui-components/src/v2/VCalculatorInput/index.jsx';
import VBox from '@voly/ui-components/src/v2/VBox.jsx';
import VInputGroup from '@voly/ui-components/src/v2/VInputGroup.jsx';
import VNumberInput, {
  NumberInputProps,
  NumberInputSize,
} from '@voly/ui-components/src/v2/VNumberInput';
import type { FocusEvent, KeyboardEvent, ReactElement, ReactNode } from 'react';
import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { translate } from 'src/app/project/features/translate/translate';
import FieldLockedIcon from './FieldLockedIcon';

export type TUnitNumberInputProps = NumberInputProps & {
  unit?: ReactNode;
  calculations?: boolean;
  locked?: boolean;
  onCommit?: (value: number | null) => void;
};

const CALCULATION_TRIGGER_KEY = '=';
const CALCULATION_PLACEHOLDER = '10 + 5';
const ATTACHED_RADIUS = 0;
const START_ELEMENT_WIDTH = 'var(--input-height)';

const INPUT = '& input';
const NUMBER_INPUT_ROOT = '& [data-scope="number-input"][data-part="root"]';
const STEPPER = '& [data-part="control"]';

const ATTACHED_INPUT_STYLE = { [INPUT]: { borderEndRadius: ATTACHED_RADIUS } };

const PADDING_END_WITHOUT_STEPPER: Record<NumberInputSize, string> = {
  xs: '2',
  sm: '2.5',
  md: '3',
  lg: '4',
};

const clampNumber = (value: number, min: number, max: number | undefined): number => {
  const lowerBounded = Math.max(value, min);
  return max === undefined ? lowerBounded : Math.min(lowerBounded, max);
};

const roundToDecimals = (value: number, decimals: number | undefined): number => {
  if (decimals === undefined) {
    return value;
  }
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

const toNullable = (valueAsNumber: number): number | null =>
  Number.isFinite(valueAsNumber) ? valueAsNumber : null;

const UnitNumberInput = ({
  unit,
  calculations = false,
  locked = false,
  onCommit,
  ...props
}: TUnitNumberInputProps): ReactElement => {
  const {
    size = 'lg',
    disabled: disabledProp = false,
    min,
    max,
    maxDecimalPlaces,
    formatOptions,
    textAlign,
    value,
    onValueChange,
  } = props;
  const fieldDisabled: boolean = useFieldContext()?.disabled ?? false;
  const disabled: boolean = disabledProp || fieldDisabled;
  const readOnly = locked || (props.readOnly ?? false);
  const [expression, setExpression] = useState<string | null>(null);
  const numberInputBoxRef = useRef<HTMLDivElement>(null);
  const calculatorInputRef = useRef<HTMLInputElement>(null);
  const calculatorOpen = useRef(false);
  const calculatorSettled = useRef(false);
  const refocusNumberInput = useRef(false);
  const latestValue = useRef<number | null>(null);
  latestValue.current = value === null ? null : toNullable(value);
  const hasUnit = unit !== undefined;
  const canCalculate = calculations && !readOnly && !disabled;
  const maximumFractionDigits = formatOptions?.maximumFractionDigits ?? maxDecimalPlaces;

  const handleValueChange = (valueAsNumber: number): void => {
    latestValue.current = toNullable(valueAsNumber);
    onValueChange(valueAsNumber);
  };

  const settle = (unsettled: number | null): number | null =>
    unsettled === null
      ? null
      : clampNumber(roundToDecimals(unsettled, maximumFractionDigits), min, max);

  const onFieldBlur = (event: FocusEvent<HTMLDivElement>): void => {
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    onCommit?.(settle(latestValue.current));
  };

  const focusNumberInput = (): void => {
    numberInputBoxRef.current?.querySelector('input')?.focus();
  };

  const openCalculator = (): void => {
    calculatorOpen.current = true;
    calculatorSettled.current = false;
    flushSync(() => setExpression(''));
    calculatorInputRef.current?.focus();
  };

  const closeCalculator = (): void => {
    calculatorOpen.current = false;
    flushSync(() => setExpression(null));
    if (!refocusNumberInput.current) {
      return;
    }
    refocusNumberInput.current = false;
    focusNumberInput();
  };

  const commitResult = (result: string): void => {
    const parsed = Number(result);
    if (result.trim() === '' || !Number.isFinite(parsed)) {
      return;
    }
    handleValueChange(settle(parsed) ?? parsed);
  };

  const onNumberInputKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter') {
      event.currentTarget.querySelector('input')?.blur();
      return;
    }
    if (!canCalculate || event.key !== CALCULATION_TRIGGER_KEY) {
      return;
    }
    event.preventDefault();
    openCalculator();
  };

  const onCalculatorValueChange = (next: string): void => {
    if (!calculatorOpen.current) {
      return;
    }
    if (!calculatorSettled.current) {
      setExpression(next);
      return;
    }
    commitResult(next);
    closeCalculator();
  };

  const onCalculatorBlur = (): void => {
    calculatorSettled.current = true;
  };

  const onCalculatorKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      refocusNumberInput.current = true;
      event.currentTarget.blur();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      refocusNumberInput.current = true;
      closeCalculator();
    }
  };

  const unitAddon = hasUnit ? (
    <VBox as="span" data-testid="unit-addon">
      {unit}
    </VBox>
  ) : undefined;

  if (expression === null) {
    const numberInput = (
      <VNumberInput
        {...props}
        disabled={disabled}
        size={size}
        readOnly={readOnly}
        onValueChange={handleValueChange}
      />
    );
    const grouped = hasUnit || locked;
    const numberInputStyle: SystemStyleObject = {
      [NUMBER_INPUT_ROOT]: { flex: 1 },
      [STEPPER]: { display: 'none' },
      [INPUT]: {
        pe: PADDING_END_WITHOUT_STEPPER[size],
        borderEndRadius: hasUnit ? ATTACHED_RADIUS : undefined,
        ps: locked ? START_ELEMENT_WIDTH : undefined,
      },
    };

    return (
      <Box
        ref={numberInputBoxRef}
        css={numberInputStyle}
        onKeyDownCapture={onNumberInputKeyDown}
        onBlur={onFieldBlur}
        flex={1}
      >
        {grouped ? (
          <VInputGroup
            startElement={locked ? <FieldLockedIcon /> : undefined}
            endAddon={unitAddon}
            endAddonProps={{ size }}
          >
            {numberInput}
          </VInputGroup>
        ) : (
          numberInput
        )}
      </Box>
    );
  }

  const calculatorField = (
    <Box flex="1" css={hasUnit ? ATTACHED_INPUT_STYLE : undefined} onBlur={onFieldBlur}>
      <VCalculatorInput
        ref={calculatorInputRef}
        size={size}
        value={expression}
        textAlign={textAlign}
        placeholder={CALCULATION_PLACEHOLDER}
        aria-label={translate('calculation_enabled')}
        onValueChange={onCalculatorValueChange}
        onBlur={onCalculatorBlur}
        onKeyDown={onCalculatorKeyDown}
      />
    </Box>
  );

  if (!hasUnit) {
    return calculatorField;
  }

  return (
    <Group attached width="full">
      {calculatorField}
      <InputAddon size={size}>{unitAddon}</InputAddon>
    </Group>
  );
};

export default UnitNumberInput;
